
import Vue from 'vue';
import { message } from 'ant-design-vue';
import { uploadFileToOss } from './ossUpload';

const MB = 1024 * 1024;

interface MediaMeta {
  width?: number;
  height?: number;
  duration?: number;
}

interface UploadedWorkFile extends MediaMeta {
  id: string;
  name: string;
  file: File;
  extension: string;
  size: number;
  url: string;
  uploadUrl?: string;
}

export default Vue.extend({
  name: 'WorkFileUploader',
  props: {
    label: {
      type: String,
      required: true,
    },
    required: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: '',
    },
    allowedExtensions: {
      type: Array,
      default: () => [],
    },
    maxSizeMb: {
      type: Number,
      default: undefined,
    },
    maxSizeByExt: {
      type: Object,
      default: () => ({}),
    },
    imageRule: {
      type: Object,
      default: () => ({}),
    },
    videoRule: {
      type: Object,
      default: () => ({}),
    },
    tip: {
      type: String,
      default: '',
    },
    emptyText: {
      type: String,
      default: '点击或拖拽文件到此处上传',
    },
    countText: {
      type: String,
      default: '最多上传1个文件',
    },
  },
  data() {
    return {
      validating: false,
      uploadErrors: [] as string[],
      uploadedFile: null as UploadedWorkFile | null,
    };
  },
  computed: {
    triggerText(): string {
      if (this.validating) {
        return '校验中';
      }
      return this.uploadedFile ? '已上传1个文件' : this.emptyText;
    },
    previewType(): string {
      if (!this.uploadedFile) {
        return '';
      }
      if (this.isImageExtension(this.uploadedFile.extension)) {
        return 'image';
      }
      return '';
    },
    fileMetaText(): string {
      if (!this.uploadedFile) {
        return '';
      }

      const parts = [this.formatSize(this.uploadedFile.size)];
      if (this.uploadedFile.width && this.uploadedFile.height) {
        parts.push(`${this.uploadedFile.width}×${this.uploadedFile.height}px`);
      }
      if (this.uploadedFile.duration) {
        parts.push(this.formatDuration(this.uploadedFile.duration));
      }
      return parts.join(' · ');
    },
  },
  beforeDestroy() {
    this.revokeFileUrl();
  },
  methods: {
    hasFiles(): boolean {
      return Boolean(this.uploadedFile);
    },
    isValidating(): boolean {
      return this.validating;
    },
    clear() {
      this.revokeFileUrl();
      this.uploadedFile = null;
      this.uploadErrors = [];
      this.emitChange();
    },
    setInitialFile(url: string) {
      this.clear();
      if (!url) {
        return;
      }
      const name = this.getFileNameFromUrl(url) || '已上传文件';
      this.uploadedFile = {
        id: `remote-${url}`,
        name,
        file: null as any,
        extension: this.getExtension(name),
        size: 0,
        url,
        uploadUrl: url,
      };
      this.emitChange();
    },
    getFileNameFromUrl(url: string): string {
      const path = url.split('?')[0].split('#')[0];
      try {
        return decodeURIComponent(path.split('/').pop() || '');
      } catch (err) {
        return path.split('/').pop() || '';
      }
    },
    emitChange() {
      this.$emit('change', this.uploadedFile);
    },
    setValidating(validating: boolean) {
      this.validating = validating;
      this.$emit('validating-change', validating);
    },
    openFilePicker() {
      if (this.uploadedFile) {
        message.warning(`${this.label}最多上传1个，请删除后重新上传`);
        return;
      }

      const input = this.$refs.fileInput as HTMLInputElement;
      if (input && !this.validating && !this.disabled) {
        input.click();
      }
    },
    handleFileChange(event: Event) {
      const input = event.target as HTMLInputElement;
      this.validateFiles(input.files);
      input.value = '';
    },
    handleDrop(event: DragEvent) {
      if (this.validating || this.disabled) {
        return;
      }
      if (this.uploadedFile) {
        message.warning(`${this.label}最多上传1个，请删除后重新上传`);
        return;
      }
      this.validateFiles(event.dataTransfer ? event.dataTransfer.files : null);
    },
    async validateFiles(fileList: FileList | null) {
      if (!fileList || !fileList.length) {
        return;
      }
      if (fileList.length > 1) {
        this.uploadErrors = [`${this.label}最多上传1个`];
        message.error(`${this.label}最多上传1个`);
        return;
      }

      this.setValidating(true);
      this.uploadErrors = [];

      try {
        const file = await this.validateWorkFile(fileList[0]);
        this.revokeFileUrl();
        this.uploadedFile = file;
        message.success(`已添加${this.label}`);
      } catch (err) {
        this.uploadErrors = [
          err instanceof Error ? err.message : String(err),
        ];
        message.error(`${this.label}不符合上传要求`);
      } finally {
        this.setValidating(false);
        this.emitChange();
      }
    },
    async validateWorkFile(file: File): Promise<UploadedWorkFile> {
      const extension = this.getExtension(file.name);
      const allowedExtensions = (this.allowedExtensions as string[]).map((item) =>
        String(item).toLowerCase(),
      );

      if (allowedExtensions.length && allowedExtensions.indexOf(extension) === -1) {
        throw new Error(`仅支持${allowedExtensions.join('、').toUpperCase()}格式`);
      }

      const maxSize = this.getMaxSizeMb(extension);
      if (maxSize && file.size > maxSize * MB) {
        throw new Error(`文件大小不能超过${maxSize}MB`);
      }

      const url = URL.createObjectURL(file);
      const meta: MediaMeta = {};

      try {
        if (this.isImageExtension(extension)) {
          Object.assign(meta, await this.getImageSize(url));
          this.validateImageRule(meta, extension);
        } else if (this.isVideoExtension(extension)) {
          Object.assign(meta, await this.getVideoMeta(url));
          this.validateVideoRule(meta);
        }

        return {
          id: `${Date.now()}-${Math.random()}`,
          name: file.name,
          file,
          extension,
          size: file.size,
          url,
          ...meta,
        };
      } catch (err) {
        URL.revokeObjectURL(url);
        throw err;
      }
    },
    validateImageRule(meta: MediaMeta, extension: string) {
      const rule = this.imageRule as any;
      if (!rule || !Object.keys(rule).length || !meta.width || !meta.height) {
        return;
      }

      const ignoreExtensions = (rule.ignoreExtensions || []).map((item: string) =>
        String(item).toLowerCase(),
      );
      if (ignoreExtensions.indexOf(extension) > -1) {
        return;
      }

      if (rule.exactWidth && rule.exactHeight) {
        if (meta.width !== rule.exactWidth || meta.height !== rule.exactHeight) {
          throw new Error(`图片尺寸需为${rule.exactWidth}×${rule.exactHeight}px`);
        }
      }

      const shortSide = Math.min(meta.width, meta.height);
      const longSide = Math.max(meta.width, meta.height);
      if (rule.minLongRatio && longSide / shortSide < rule.minLongRatio) {
        throw new Error(`总长宽比不低于1:${rule.minLongRatio}`);
      }
      if (rule.maxShortSide && shortSide > rule.maxShortSide) {
        throw new Error(`长图短边不超过${rule.maxShortSide}px`);
      }
    },
    validateVideoRule(meta: MediaMeta) {
      const rule = this.videoRule as any;
      if (!rule || !Object.keys(rule).length) {
        return;
      }

      if (rule.maxDurationSeconds && meta.duration && meta.duration > rule.maxDurationSeconds) {
        throw new Error(`视频时长不能超过${rule.maxDurationSeconds}秒`);
      }
      if (rule.exactWidth && rule.exactHeight) {
        if (meta.width !== rule.exactWidth || meta.height !== rule.exactHeight) {
          throw new Error(`视频尺寸需为${rule.exactWidth}×${rule.exactHeight}px`);
        }
      }
    },
    getImageSize(url: string): Promise<MediaMeta> {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
          resolve({
            width: image.naturalWidth || image.width,
            height: image.naturalHeight || image.height,
          });
        };
        image.onerror = () => reject(new Error('图片读取失败'));
        image.src = url;
      });
    },
    getVideoMeta(url: string): Promise<MediaMeta> {
      return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            duration: video.duration,
          });
        };
        video.onerror = () => reject(new Error('视频读取失败'));
        video.src = url;
      });
    },
    removeFile() {
      if (this.disabled) {
        return;
      }
      this.revokeFileUrl();
      this.uploadedFile = null;
      this.uploadErrors = [];
      this.emitChange();
    },
    revokeFileUrl() {
      if (this.uploadedFile) {
        URL.revokeObjectURL(this.uploadedFile.url);
      }
    },
    getExtension(fileName: string): string {
      const parts = fileName.split('.');
      return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
    },
    getMaxSizeMb(extension: string): number | undefined {
      const maxSizeByExt = this.maxSizeByExt as { [key: string]: number };
      return maxSizeByExt[extension] || (this.maxSizeMb as number | undefined);
    },
    isImageExtension(extension: string): boolean {
      return ['jpg', 'jpeg', 'png', 'gif'].indexOf(extension) > -1;
    },
    isVideoExtension(extension: string): boolean {
      return extension === 'mp4';
    },
    formatSize(size: number): string {
      return `${(size / MB).toFixed(size > MB ? 1 : 2)}MB`;
    },
    formatDuration(duration: number): string {
      const totalSeconds = Math.round(duration);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    },
    async uploadCurrentFileToOss(fileItem: UploadedWorkFile): Promise<string> {
      if (fileItem.uploadUrl) {
        return fileItem.uploadUrl;
      }

      const uploadUrl = await uploadFileToOss(fileItem);
      this.$set(fileItem, 'uploadUrl', uploadUrl);
      return uploadUrl;
    },
    async uploadToOss(): Promise<string[]> {
      if (!this.uploadedFile) {
        return [];
      }
      return [await this.uploadCurrentFileToOss(this.uploadedFile)];
    },
  },
});
